import {cx} from '@emotion/css';
import React, {ReactElement, useState} from 'react';
import {useIntl} from 'react-intl';
import {Values as ResultValues} from '../../../data/useBusinessLoanCalculator/useBusinessLoanCalculator';
import getCurrencyFromLocale from '../../../localeUtils/getCurrencyFromLocale';
import {Device, useDevice} from '../../../RootProvider/DeviceContext';
import {useLocale} from '../../../RootProvider/LocaleContext';
import AccordionItem from '../../DesignSystem/Accordion/AccordionItem/AccordionItem';
import {AccordionItemIconVariation} from '../../DesignSystem/Accordion/AccordionItemIcon/AccordionItemIcon';
import CalculatorResultItem from './CalculatorResultItem/CalculatorResultItem';
import styles from './CalculatorResults.styles';

interface Props {
  values: ResultValues;
}

function CalculatorResults({values}: Props): ReactElement {
  const {formatMessage} = useIntl();
  const locale = useLocale();
  const {monthlyPayable, monthlyPayableInterest, termLength, totalPayable, totalPayableInterest} = values;
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const device = useDevice();
  const isMobile = device === Device.MOBILE;
  const buttonTitle = isOpen
    ? formatMessage({id: 'businessLoanCalculator.buttonCollapse.hide.title'})
    : formatMessage({id: 'businessLoanCalculator.buttonCollapse.show.title'});

  const handleItemCollapsedState = () => {
    setIsOpen(!isOpen);
  };

  const currencyTemplate = (value: string): string => {
    const currency = getCurrencyFromLocale(locale);
    return `${currency}${value}`;
  };

  const monthsTemplate = (value: string): string =>
    formatMessage({id: 'businessLoanCalculator.results.months'}, {value});

  return (
    <div className={cx(styles.calculatorResults)}>
      <AccordionItem
        labelClass={cx(styles.collapseButton(!isMobile))}
        itemClass={cx(styles.wrapper)}
        contentClass={cx(styles.wrapperInner)}
        isOpen={!isMobile || isOpen}
        label={buttonTitle}
        handleItemCollapsedState={handleItemCollapsedState}
        iconVariation={AccordionItemIconVariation.PLUS_MINUS}
      >
        <CalculatorResultItem
          title={formatMessage({id: 'businessLoanCalculator.results.monthlyPayable'})}
          value={monthlyPayable}
          template={currencyTemplate}
        />
        <CalculatorResultItem
          title={formatMessage({id: 'businessLoanCalculator.results.monthlyPayableInterest'})}
          value={monthlyPayableInterest}
          template={currencyTemplate}
        />
        <CalculatorResultItem
          title={formatMessage({id: 'businessLoanCalculator.results.totalPayableInterest'})}
          value={totalPayableInterest}
          template={currencyTemplate}
        />
        <CalculatorResultItem
          title={formatMessage({id: 'businessLoanCalculator.results.termLength'})}
          value={termLength}
          template={monthsTemplate}
        />
        <CalculatorResultItem
          bordered
          title={formatMessage({id: 'businessLoanCalculator.results.totalPayable'})}
          value={totalPayable}
          template={currencyTemplate}
        />
      </AccordionItem>
    </div>
  );
}

export default CalculatorResults;
